import React, { useState, useCallback, memo } from "react";
import "@/apps/forge/components/treeview/ForgeTreeView.scss";

export interface ListItemNodeProps {
  // Core node data
  id: string;
  name: string;
  hasChildren?: boolean;
  isExpanded?: boolean;
  isSelected?: boolean;
  isFocused?: boolean;
  isLoading?: boolean;
  hasContextMenu?: boolean;
  depth?: number;
  
  // Visual properties
  icon?: string;
  iconType?: 'folder' | 'file' | 'expanded';
  fileType?: string;
  className?: string;
  /** When set, rendered instead of the plain `name` string. */
  labelContent?: React.ReactNode;
  
  // Event handlers
  onToggle?: () => void;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onContextMenu?: (event: React.MouseEvent) => void;
  onSelect?: (nodeId: string) => void;
  
  // Data attributes for external use
  dataAttributes?: Record<string, any>;
  
  // Children
  children?: React.ReactNode;
}

export const ListItemNode = memo(function ListItemNode(props: ListItemNodeProps) {
  const {
    id,
    name,
    hasChildren = false,
    isExpanded = false,
    isSelected = false,
    isFocused = false,
    isLoading = false,
    hasContextMenu = false,
    depth = 0,
    icon,
    iconType = 'file',
    fileType,
    className = '',
    labelContent,
    onToggle,
    onClick,
    onDoubleClick,
    onContextMenu,
    onSelect,
    dataAttributes = {},
    children
  } = props;

  const [isHovered, setIsHovered] = useState(false);

  // Get file extension for icon styling
  const getFileExtension = (name: string): string => {
    if (!name) return '';
    const parts = name.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  };

  // Get file type class for styling
  const getFileTypeClass = (name: string): string => {
    if (!name) return 'file';
    const ext = getFileExtension(name);
    if (ext) {
      return `file-${ext}`;
    }
    return 'file';
  };

  // Get icon for the node
  const getIcon = (): string => {
    if (icon) {
      return icon;
    }
    
    if (iconType === 'folder') {
      return isExpanded ? 'fa-folder-open' : 'fa-folder';
    }
    
    if (!name) return 'fa-file';
    const ext = getFileExtension(name);
    switch (ext) {
      case 'ts': return 'fa-file-code';
      case 'tsx': return 'fa-file-code';
      case 'js': return 'fa-file-code';
      case 'jsx': return 'fa-file-code';
      case 'css': return 'fa-file-code';
      case 'scss': return 'fa-file-code';
      case 'html': return 'fa-file-code';
      case 'json': return 'fa-file-code';
      case 'md': return 'fa-file-lines';
      case 'png': return 'fa-file-image';
      case 'jpg': return 'fa-file-image';
      case 'jpeg': return 'fa-file-image';
      case 'gif': return 'fa-file-image';
      case 'svg': return 'fa-file-image';
      case 'ico': return 'fa-file-image';
      case 'psd': return 'fa-file-image';
      case 'wav': return 'fa-file-audio';
      case 'mp3': return 'fa-file-audio';
      case 'ogg': return 'fa-file-audio';
      case 'nss': return 'fa-file-code';
      case 'ncs': return 'fa-file-code';
      case 'dlg': return 'fa-file-code';
      case 'res': return 'fa-file-code';
      case 'utc': return 'fa-file-code';
      case 'utd': return 'fa-file-code';
      case 'ute': return 'fa-file-code';
      case 'uti': return 'fa-file-code';
      case 'utm': return 'fa-file-code';
      case 'utp': return 'fa-file-code';
      case 'uts': return 'fa-file-code';
      case 'utt': return 'fa-file-code';
      case 'utw': return 'fa-file-code';
      case 'bic': return 'fa-file-code';
      case 'bik': return 'fa-file-video';
      case 'tga': return 'fa-file-image';
      case 'dds': return 'fa-file-image';
      case 'tpc': return 'fa-file-image';
      case 'erf': return 'fa-file-archive';
      case 'rim': return 'fa-file-archive';
      case 'mod': return 'fa-file-archive';
      case 'sav': return 'fa-file-archive';
      case 'hak': return 'fa-file-archive';
      case 'nwm': return 'fa-file-archive';
      default: return 'fa-file';
    }
  };

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren && onToggle) {
      onToggle();
    }
  }, [hasChildren, onToggle]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
    // if (onSelect) {
    //   onSelect(id);
    // }
  }, [id, onClick, onSelect]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDoubleClick) {
      onDoubleClick();
    }
  }, [onDoubleClick]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onContextMenu) {
      onContextMenu(e);
    }
  }, [onContextMenu]);

  const getVisibleNodeElements = (currentElement: HTMLElement): HTMLElement[] => {
    const treeRoot = currentElement.closest('.forgeTreeView');
    if (!treeRoot) return [];
    const all = Array.from(treeRoot.querySelectorAll('.tree-node-content')) as HTMLElement[];
    return all.filter((el) => el.offsetParent !== null);
  };

  const focusNodeAtIndex = (nodes: HTMLElement[], index: number) => {
    if (index < 0 || index >= nodes.length) return;
    nodes[index].focus();
  };

  const focusParentNode = (currentElement: HTMLElement) => {
    const parentLi = currentElement.closest('ul.tree-children')?.closest('li.tree-item');
    const parentNode = parentLi?.querySelector(':scope > .tree-node-content') as HTMLElement | null;
    if (parentNode) {
      parentNode.focus();
    }
  };

  const focusFirstChildNode = (currentElement: HTMLElement) => {
    const currentLi = currentElement.closest('li.tree-item');
    const firstChild = currentLi?.querySelector(':scope > ul.tree-children.expanded > li.tree-item > .tree-node-content') as HTMLElement | null;
    if (firstChild) {
      firstChild.focus();
    }
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const currentElement = e.currentTarget as HTMLElement;
    const visibleNodes = getVisibleNodeElements(currentElement);
    const currentIndex = visibleNodes.indexOf(currentElement);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusNodeAtIndex(visibleNodes, currentIndex + 1);
      break;
      case 'ArrowUp':
        e.preventDefault();
        focusNodeAtIndex(visibleNodes, currentIndex - 1);
      break;
      case 'ArrowRight':
        e.preventDefault();
        if (hasChildren && !isExpanded && onToggle) {
          onToggle();
        } else if (hasChildren && isExpanded) {
          focusFirstChildNode(currentElement);
        }
      break;
      case 'ArrowLeft':
        e.preventDefault();
        if (hasChildren && isExpanded && onToggle) {
          onToggle();
        } else {
          focusParentNode(currentElement);
        }
      break;
      case 'Home':
        e.preventDefault();
        focusNodeAtIndex(visibleNodes, 0);
      break;
      case 'End':
        e.preventDefault();
        focusNodeAtIndex(visibleNodes, visibleNodes.length - 1);
      break;
      case 'Enter':
        e.preventDefault();
        if (onDoubleClick) {
          onDoubleClick();
        } else if (onClick) {
          onClick();
        }
      break;
      case ' ':
        e.preventDefault();
        if (onClick) {
          onClick();
        }
      break;
      default:
      break;
    }
  }, [hasChildren, isExpanded, onToggle, onClick]);

  const fileTypeClass = getFileTypeClass(name);
  const iconClass = getIcon();

  return (
    <li 
      className={`tree-item ${fileTypeClass} ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''} ${className}`.trim()}
      {...dataAttributes}
    >
      {/* Node content wrapper - arrow, icon, and label */}
      <div 
        className="tree-node-content"
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        tabIndex={0}
      >
        {/* Expand/Collapse Arrow for folders */}
        {hasChildren && (
          <span 
            className={`tree-arrow ${isExpanded ? 'expanded' : ''}`}
            onClick={handleToggle}
            role="button"
            tabIndex={-1}
            title={isExpanded ? 'Collapse' : 'Expand'}
          />
        )}
        
        {/* Icon */}
        <span className={`tree-icon ${iconType}`}>
          <i className={`fa-solid ${iconClass}`} />
        </span>
        
        {/* Label */}
        <span className="tree-label" title={name}>
          {labelContent ?? name}
        </span>
      </div>
      
      {/* Children - flows to next line */}
      {hasChildren && children && (
        <ul className={`tree-children ${isExpanded ? 'expanded' : ''}`}>
          {children}
        </ul>
      )}
    </li>
  );
});
